import { useCallback, useEffect, useState } from 'react';

interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  mozFullScreenElement?: Element | null;
  mozCancelFullScreen?: () => Promise<void>;
}

interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
  mozRequestFullScreen?: () => Promise<void>;
}

/**
 * Cross-vendor Fullscreen API hook.
 *
 * Returns `[isFullscreen, enter, exit, toggle]`. Vendor-prefixed
 * fallbacks for Safari (webkit) and Firefox (moz) keep older browsers
 * happy. Tracks the document element by default; pass a target ref to
 * fullscreen a specific subtree (rare in classy — the entire app
 * benefits from going fullscreen at once).
 */
export interface UseFullscreenResult {
  isFullscreen: boolean;
  enter(): Promise<void>;
  exit(): Promise<void>;
  toggle(): Promise<void>;
  /** True if the runtime exposes any Fullscreen API at all. */
  supported: boolean;
}

function currentFullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? doc.mozFullScreenElement ?? null;
}

export function useFullscreen(): UseFullscreenResult {
  const [isFullscreen, setIsFullscreen] = useState(() => currentFullscreenElement() !== null);

  useEffect(() => {
    function onChange() {
      setIsFullscreen(currentFullscreenElement() !== null);
    }
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    document.addEventListener('mozfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      document.removeEventListener('mozfullscreenchange', onChange);
    };
  }, []);

  const supported =
    typeof document !== 'undefined' &&
    (document.documentElement.requestFullscreen !== undefined ||
      (document.documentElement as FullscreenElement).webkitRequestFullscreen !== undefined ||
      (document.documentElement as FullscreenElement).mozRequestFullScreen !== undefined);

  const enter = useCallback(async () => {
    const el = document.documentElement as FullscreenElement;
    if (el.requestFullscreen) await el.requestFullscreen();
    else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    else if (el.mozRequestFullScreen) await el.mozRequestFullScreen();
  }, []);

  const exit = useCallback(async () => {
    const doc = document as FullscreenDocument;
    if (doc.exitFullscreen) await doc.exitFullscreen();
    else if (doc.webkitExitFullscreen) await doc.webkitExitFullscreen();
    else if (doc.mozCancelFullScreen) await doc.mozCancelFullScreen();
  }, []);

  const toggle = useCallback(async () => {
    if (currentFullscreenElement()) {
      await exit();
    } else {
      await enter();
    }
  }, [enter, exit]);

  return { isFullscreen, enter, exit, toggle, supported };
}
